import { clamp01 } from "../math/Geometry.js";
import { tuning } from "../state/tuning.js";
import type { PlayerState } from "../state/types.js";

export const FIXED_DT = 1 / 60;

export type SkatingInput = {
  moveX: number; // [-1, 1]
  moveY: number;
  /**
   * Precomputed sqrt(moveX^2 + moveY^2). Hot-path callers (SimTick) supply
   * this to share the sqrt with the stickDir update. Tests / non-hot callers
   * may omit it; applySkating then computes it locally.
   */
  moveMag?: number;
  turbo: boolean;
};

/**
 * Mutates player: updates vel, stamina, and moveDir. Does NOT integrate position
 * (caller does that after collision resolution). Zero-allocation hot path —
 * all intermediates are scalar locals (see writing-zero-alloc-inner-loop skill).
 */
export function applySkating(player: PlayerState, input: SkatingInput): void {
  if (player.action === "Stunned") return;

  const t = tuning.player;

  // Turbo needs held input and remaining stamina.
  const effectiveTurbo = input.turbo && player.stamina > 0;

  let maxSpeed = effectiveTurbo ? t.turboMaxSpeed : t.maxSpeed;
  if (player.isProtecting) {
    maxSpeed = t.maxSpeed * t.protectMaxSpeedMultiplier;
  }
  const inputMag =
    input.moveMag ?? Math.sqrt(input.moveX * input.moveX + input.moveY * input.moveY);
  let moveDirX = 0;
  let moveDirY = 0;
  if (inputMag > 0.001) {
    moveDirX = input.moveX / inputMag;
    moveDirY = input.moveY / inputMag;
  }

  const desiredScale = maxSpeed * Math.min(inputMag, 1);
  const desiredVelX = moveDirX * desiredScale;
  const desiredVelY = moveDirY * desiredScale;
  const steerX = desiredVelX - player.vel.x;
  const steerY = desiredVelY - player.vel.y;
  let acceleration: number = t.acceleration;
  const currentSpeed = Math.sqrt(player.vel.x * player.vel.x + player.vel.y * player.vel.y);
  if (inputMag <= 0.001) {
    acceleration = t.brake;
  } else if (currentSpeed > 0.001) {
    const velDirX = player.vel.x / currentSpeed;
    const velDirY = player.vel.y / currentSpeed;
    const turnDot = velDirX * moveDirX + velDirY * moveDirY;
    if (turnDot < 0.35) acceleration = t.brake;
  }
  const accelLimit = acceleration * FIXED_DT;
  const steerLenSq = steerX * steerX + steerY * steerY;
  let appliedSteerX = steerX;
  let appliedSteerY = steerY;
  if (steerLenSq > accelLimit * accelLimit) {
    const steerLen = Math.sqrt(steerLenSq);
    const scale = accelLimit / steerLen;
    appliedSteerX = steerX * scale;
    appliedSteerY = steerY * scale;
  }
  player.vel.x += appliedSteerX;
  player.vel.y += appliedSteerY;

  // Apply drag.
  player.vel.x *= t.iceDrag;
  player.vel.y *= t.iceDrag;

  // Clamp final speed.
  const speed = Math.sqrt(player.vel.x * player.vel.x + player.vel.y * player.vel.y);
  if (speed > maxSpeed) {
    const clampScale = maxSpeed / speed;
    player.vel.x *= clampScale;
    player.vel.y *= clampScale;
  }

  // Stamina: deplete on turbo-while-moving, recover otherwise.
  if (player.isProtecting || (effectiveTurbo && inputMag > 0.1)) {
    player.stamina = clamp01(player.stamina - t.staminaDepleteRate * FIXED_DT);
  } else {
    player.stamina = clamp01(player.stamina + t.staminaRecoverRate * FIXED_DT);
  }

  player.moveDir.x = moveDirX;
  player.moveDir.y = moveDirY;
}
